/**
 * Mükellef durumu — TC/VKN → e-Fatura mükellefi mi, yoksa e-Arşiv mi?
 *
 * Kaynak: `mukellefDurumu/{taxId}` (Kolaysoft arşivinden üretiliyor,
 * scripts/mukellef-durumu-olustur.cjs). Canlı Banka tablosunda para gönderen
 * müşterinin belge tipini rozet olarak göstermek için kullanılır.
 *
 * DİKKAT: TCKN'nin 11 haneli olması "e-Arşiv" demek DEĞİLDİR — arşivdeki
 * e-Fatura mükelleflerinin 52'sinden 43'ü 11 haneli gerçek kişi. Bu yüzden
 * tahmin üretilmez; kayıt yoksa "bilinmiyor" döner.
 */
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../firebase';

export const MUKELLEF_DURUM = {
  EFATURA: 'EFATURA',
  EARSIV: 'EARSIV',
  BILINMIYOR: 'BILINMIYOR'
};

/** Aynı TC tekrar tekrar sorulmasın; oturum boyunca bellekte tutulur. */
const cache = new Map();
const pending = new Map();

const normalize = (value) => {
  const digits = String(value ?? '').replace(/\D/g, '');
  return digits.length === 10 || digits.length === 11 ? digits : '';
};

async function fetchOne(taxId) {
  try {
    const snap = await getDoc(doc(db, 'mukellefDurumu', taxId));
    if (!snap.exists()) {
      return { taxId, durum: MUKELLEF_DURUM.BILINMIYOR };
    }
    const data = snap.data() || {};
    return {
      taxId,
      durum: data.isEfatura ? MUKELLEF_DURUM.EFATURA : MUKELLEF_DURUM.EARSIV,
      title: data.title || '',
      lastEfaturaDate: data.lastEfaturaDate || '',
      lastEarsivDate: data.lastEarsivDate || '',
      source: data.source || ''
    };
  } catch (err) {
    // Okunamazsa rozet gösterilmez; tablo eskisi gibi çalışır.
    return { taxId, durum: MUKELLEF_DURUM.BILINMIYOR, error: err?.message || 'okunamadı' };
  }
}

/** Tek TC/VKN sorgular (bellekten döner). */
export async function getMukellefDurumu(taxIdRaw) {
  const taxId = normalize(taxIdRaw);
  if (!taxId) return null;
  if (cache.has(taxId)) return cache.get(taxId);
  if (pending.has(taxId)) return pending.get(taxId);
  const p = fetchOne(taxId).then((sonuc) => {
    cache.set(taxId, sonuc);
    pending.delete(taxId);
    return sonuc;
  });
  pending.set(taxId, p);
  return p;
}

/**
 * Tablodaki satırlar için toplu sorgu; sonuç Map(taxId → durum).
 *
 * Sunucu Kolaysoft'a CANLI sorar: hiç sorulmamış TC her zaman, bugün sorulmuş
 * olan tekrar sorulmaz (GİB mükellef listesi gün içinde değişmiyor). Sunucuya
 * ulaşılamazsa Firestore tablosundaki son bilinen değere düşülür, böylece
 * rozetler büsbütün kaybolmaz.
 */
export async function getMukellefDurumlari(taxIds = [], sube) {
  const uniq = [...new Set(taxIds.map(normalize).filter(Boolean))];
  if (!uniq.length) return new Map();
  const map = new Map();
  const sorulacak = [];
  for (const id of uniq) {
    if (cache.has(id)) map.set(id, cache.get(id));
    else sorulacak.push(id);
  }
  if (!sorulacak.length) return map;

  if (sube) {
    try {
      const { checkInvoiceRecipientsBatch } = await import('./invoiceEdmApi');
      const res = await checkInvoiceRecipientsBatch({ sube, taxIds: sorulacak });
      const gelen = res?.sonuclar || {};
      for (const id of sorulacak) {
        const r = gelen[id];
        if (!r) continue;
        const sonuc = {
          taxId: id,
          durum: r.isEfatura ? MUKELLEF_DURUM.EFATURA : MUKELLEF_DURUM.EARSIV,
          title: r.title || '',
          alias: r.alias || '',
          source: r.taze ? 'kolaysoft_canli' : 'kayit'
        };
        cache.set(id, sonuc);
        map.set(id, sonuc);
      }
      if (map.size === uniq.length) return map;
    } catch (_) {
      /* sunucuya ulasilamadi; asagida tabloya dusulur */
    }
  }

  const kalan = uniq.filter((id) => !map.has(id));
  const tablodan = await Promise.all(kalan.map((id) => getMukellefDurumu(id)));
  tablodan.filter(Boolean).forEach((s) => map.set(s.taxId, s));
  return map;
}

/**
 * Kayıtta yoksa Kolaysoft'a canlı sorar. Sunucu sonucu `mukellefDurumu`
 * tablosuna yazar; aynı TC bir daha sorulmaz ve bir şubenin sorgusu diğer
 * şubelere de fayda eder. Test ortamında tabloya yazılmaz.
 */
export async function sorgulaMukellef(taxIdRaw, sube) {
  const taxId = normalize(taxIdRaw);
  if (!taxId) return null;
  const { checkInvoiceRecipient } = await import('./invoiceEdmApi');
  // Elle tiklanan sorgu her zaman taze: bugun bakilmis olsa bile yeniden sorulur.
  const res = await checkInvoiceRecipient({ sube, taxId });
  const sonuc = {
    taxId,
    durum: res?.isEfatura ? MUKELLEF_DURUM.EFATURA : MUKELLEF_DURUM.EARSIV,
    title: res?.title || '',
    alias: res?.alias || '',
    source: 'kolaysoft_sorgu'
  };
  cache.set(taxId, sonuc);
  return sonuc;
}

export function mukellefRozetStili(durum) {
  if (durum === MUKELLEF_DURUM.EFATURA) {
    return { label: 'e-Fatura', bg: '#dbeafe', color: '#1d4ed8', border: '#bfdbfe' };
  }
  if (durum === MUKELLEF_DURUM.EARSIV) {
    return { label: 'e-Arşiv', bg: '#fef3c7', color: '#92400e', border: '#fcd34d' };
  }
  return { label: 'belge tipi bilinmiyor', bg: '#f1f5f9', color: '#475569', border: '#e2e8f0' };
}
